import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, User, Calendar, Clock, Mail, Linkedin, Twitter, FileText, ArrowRight } from 'lucide-react';
const StaffProfilePage = () => {
  const {
    staffId
  } = useParams();
  // Staff profiles data - in a real app, this would come from an API or database
  const staffProfiles = {
    'michael-patel': {
      id: 'michael-patel',
      name: 'Dr. Michael Patel',
      title: 'Chief Scientific Officer',
      department: 'Research & Development',
      email: 'michael.patel@calbiome.org',
      phone: '(415) 555-0123',
      linkedin: 'https://linkedin.com/in/michaelpatel',
      twitter: '@DrMichaelPatel',
      education: ['Ph.D. in Microbiology, Stanford University', 'M.S. in Biochemistry, UC Berkeley', 'B.S. in Biology, UCLA'],
      bio: `Dr. Michael Patel leads the scientific vision and product development at CalBiome, translating cutting-edge microbiome research into practical health solutions. With over 15 years of experience in microbiology and biochemistry, he specializes in the study of biofilms and their impact on gut health.
      Prior to joining CalBiome, Dr. Patel served as the Director of Microbial Sciences at BioTech Health, where he led a team researching probiotic applications for chronic health conditions. His pioneering work on biofilm disruption strategies has been published in leading scientific journals and has formed the foundation for CalBiome's innovative Microbiota Reset Protocol.
      Dr. Patel is passionate about making complex scientific concepts accessible to the public and works closely with the Afya Program to adapt evidence-based microbiome interventions for underserved communities worldwide.`,
      expertise: ['Microbiome Research', 'Biofilm Disruption', 'Probiotic Development', 'Translational Science', 'Clinical Research Design'],
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      articles: [{
        id: 'microbiota-reset-protocol',
        title: 'CalBiome Launches Revolutionary Microbiota Reset Protocol',
        date: 'June 15, 2023',
        excerpt: 'CalBiome announces the release of its groundbreaking Microbiota Reset Protocol, a comprehensive approach to gut health backed by years of research from the California Institute for Microbiome Research (CalMiR).',
        image: 'https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        readTime: '5 min read'
      }, {
        id: 'biofilm-disruption-strategies',
        title: 'Biofilm Disruption Strategies: A New Frontier in Gut Health',
        date: 'April 3, 2023',
        excerpt: 'Explore how targeting pathogenic biofilms can revolutionize probiotic effectiveness and transform approaches to microbiome health.',
        image: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        readTime: '7 min read'
      }, {
        id: 'probiotic-colonization',
        title: 'Why Most Probiotics Fail: The Colonization Challenge',
        date: 'January 22, 2023',
        excerpt: 'Understanding the barriers to effective probiotic colonization and how next-generation formulations are addressing these challenges.',
        image: 'https://images.unsplash.com/photo-1579165466741-7f35e4755183?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        readTime: '6 min read'
      }],
      publications: ['Patel, M. et al. (2023). "Biofilm Disruption and Microbial Recolonization: A Two-Phase Approach to Microbiome Restoration." Journal of Functional Medicine, 18(2), 112-128.', 'Nguyen, A. & Patel, M. (2022). "N-acetylcysteine and Tomato Phytonutrients in Biofilm Management: Mechanisms and Applications." Integrative Medicine Research, 11:345-359.', 'Patel, M., Chen, S., & Kim, J. (2021). "Comparative Analysis of Biofilm Disruptors: Efficacy and Mechanisms." Frontiers in Microbiology, 12:567890.']
    },
    'sarah-chen': {
      id: 'sarah-chen',
      name: 'Dr. Sarah Chen',
      title: 'Research Director',
      department: 'CalMiR',
      email: 'sarah.chen@calmir.org',
      phone: '(415) 555-0456',
      linkedin: 'https://linkedin.com/in/sarahchen',
      twitter: '@DrSarahChen',
      education: ['Ph.D. in Microbiology, Stanford University', 'M.S. in Neuroscience, Johns Hopkins University', 'B.S. in Biology, MIT'],
      bio: `Dr. Sarah Chen leads research initiatives at the California Institute for Microbiome Research (CalMiR) focused on the microbiome-gut-brain connection. With a unique background spanning both microbiology and neuroscience, she has pioneered work investigating how gut bacteria influence cognitive function and mental health.
      Dr. Chen has published over 30 peer-reviewed papers in leading scientific journals and is internationally recognized for her groundbreaking research on microbial neurotransmitter production. Her work has helped establish CalMiR as a leading institution in microbiome science.
      As Research Director, Dr. Chen oversees multiple clinical trials and laboratory studies exploring the connections between gut health and neurological function, with a particular focus on developing practical applications for mental wellness and cognitive health.`,
      expertise: ['Microbiome-Gut-Brain Axis', 'Neuroinflammation', 'Clinical Trial Design', 'Microbial Metabolomics', 'Cognitive Function Research'],
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      articles: [{
        id: 'microbiome-gut-health-connection',
        title: 'The Microbiome-Gut-Brain Connection: New Research Insights',
        date: 'September 15, 2023',
        excerpt: 'Recent studies reveal fascinating connections between gut microbiome health and cognitive function, with implications for mental wellness and neurological health.',
        image: 'https://images.unsplash.com/photo-1559757175-7cb056fba93d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        readTime: '8 min read'
      }, {
        id: 'neuroinflammation-gut-bacteria',
        title: 'Neuroinflammation and Gut Bacteria: The Missing Link',
        date: 'July 7, 2023',
        excerpt: 'How specific gut bacterial communities can either promote or reduce neuroinflammation, with potential implications for neurological disorders.',
        image: 'https://images.unsplash.com/photo-1530026186672-2cd00ffc50fe?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        readTime: '9 min read'
      }, {
        id: 'microbial-metabolites-brain',
        title: 'Microbial Metabolites: The Chemical Messengers Between Gut and Brain',
        date: 'March 12, 2023',
        excerpt: 'Exploring the crucial role of bacterial metabolites in facilitating communication along the gut-brain axis.',
        image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        readTime: '7 min read'
      }],
      publications: ['Chen, S. et al. (2023). "Microbiome Modulation of Neurochemical Pathways: Implications for Cognitive Function." Journal of Microbiome Science, 12(4), 289-305.', 'Kim, L., Chen, S., & Patel, J. (2023). "Gut-Derived Metabolites and Their Impact on Neuroinflammatory Pathways." Nature Neuroscience, 26(8), 1203-1215.', 'Chen, S. & Patel, M. (2022). "Comparative Analysis of Probiotic Interventions for Cognitive Function: A Systematic Review." Journal of Nutritional Biochemistry, 103:108983.']
    }
  };
  // Get the staff profile based on the staffId parameter, or default to Dr. Michael Patel
  const profile = staffProfiles[staffId] || staffProfiles['michael-patel'];
  return <div className="min-h-screen bg-white" data-id="element-846" data-mp="lai2295pu81j">
      <Navbar data-id="element-847" data-mp="ha7vove0hfm5" />
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-teal-900 to-teal-700 text-white py-12" data-id="element-848" data-mp="hb98b4aoea5z">
        <div className="container mx-auto px-4 max-w-7xl" data-id="element-849" data-mp="cabrow5bdo6l">
          <Link to="/about" className="inline-flex items-center text-teal-100 hover:text-white mb-8" data-id="element-850" data-mp="ccxyuuoam1oa">
            <ArrowLeft size={16} className="mr-2" data-id="element-851" data-mp="2x1lyqs4v0gtz" /> Back to Team
          </Link>
          <div className="md:flex items-center" data-id="element-852" data-mp="2s2iqkytl1uhs">
            <div className="md:w-1/3 mb-6 md:mb-0" data-id="element-853" data-mp="2s0o8ds9p4ghe">
              <div className="w-48 h-48 rounded-full overflow-hidden mx-auto md:mx-0 border-4 border-white shadow-lg" data-id="element-854" data-mp="2hyualsgqgxa2">
                <img src={profile.image} alt={profile.name} className="w-full h-full object-cover" data-id="element-855" data-mp="2mygng0sngbic" />
              </div>
            </div>
            <div className="md:w-2/3 text-center md:text-left" data-id="element-856" data-mp="2wo9virtl28hu">
              <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-857" data-mp="30p273rpxgpha">
                {profile.name}
              </h1>
              <p className="text-xl text-teal-100 mb-4" data-id="element-858" data-mp="35n3sijatt0vx">
                {profile.title} at {profile.department}
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6" data-id="element-859" data-mp="3anj1kyo2rm83">
                <a href={`mailto:${profile.email}`} className="flex items-center bg-teal-800 hover:bg-teal-700 px-4 py-2 rounded-full transition-colors" data-id="element-860" data-mp="3fkrplfvwmkh9">
                  <Mail size={16} className="mr-2" data-id="element-861" data-mp="1l8l9c2o5ldi3" />
                  Email
                </a>
                <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center bg-teal-800 hover:bg-teal-700 px-4 py-2 rounded-full transition-colors" data-id="element-862" data-mp="22livxjlvdz9v">
                  <Linkedin size={16} className="mr-2" data-id="element-863" data-mp="2gksc4ja2stdu" />
                  LinkedIn
                </a>
                <a href={`https://twitter.com/${profile.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center bg-teal-800 hover:bg-teal-700 px-4 py-2 rounded-full transition-colors" data-id="element-864" data-mp="2lhr7141kd4d3">
                  <Twitter size={16} className="mr-2" data-id="element-865" data-mp="2z9p94uwuxzzr" />
                  Twitter
                </a>
              </div>
              <div className="flex flex-wrap justify-center md:justify-start gap-2" data-id="element-866" data-mp="34778puozj2v3">
                {profile.expertise.map((skill, index) => <span key={index} className="bg-teal-800 bg-opacity-50 px-3 py-1 rounded-full text-sm" data-id="element-867" data-mp="3jcle5lpt0gaf" data-mpi={index}>
                    {skill}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 max-w-7xl" data-id="element-868" data-mp="35edayw26ciqt">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-869" data-mp="5xos7lbkywkv">
          {/* Left Column - Bio and Details */}
          <div className="lg:col-span-1" data-id="element-870" data-mp="zn9j1i2gz9x">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-8" data-id="element-871" data-mp="10omxcmk1xut">
              <h2 className="text-xl font-bold text-teal-800 mb-4" data-id="element-872" data-mp="3sana3muokiqu">About</h2>
              <div className="prose text-gray-700" data-id="element-873" data-mp="3oawl9oopscoy">
                {profile.bio.split('\n\n').map((paragraph, index) => <p key={index} className="mb-4" data-id="element-874" data-mp="oprdxb32x31z" data-mpi={index}>
                    {paragraph}
                  </p>)}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-8" data-id="element-875" data-mp="2e6d2913xcffk">
              <h2 className="text-xl font-bold text-teal-800 mb-4" data-id="element-876" data-mp="2j33ltyextt5q">
                Education
              </h2>
              <ul className="space-y-2" data-id="element-877" data-mp="24epilmdm02on">
                {profile.education.map((edu, index) => <li key={index} className="flex items-start" data-id="element-878" data-mp="29cr2g9iv0616" data-mpi={index}>
                    <div className="text-teal-600 mr-2 mt-1" data-id="element-879" data-mp="1veavchh34dii">
                      <FileText size={16} data-id="element-880" data-mp="1vgx0kagi6gxk" />
                    </div>
                    <span className="text-gray-700" data-id="element-881" data-mp="20f8ael6ssrfs">{edu}</span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-882" data-mp="1qfhhup1ssmic">
              <h2 className="text-xl font-bold text-teal-800 mb-4" data-id="element-883" data-mp="1qhanq24l72pa">
                Contact Information
              </h2>
              <ul className="space-y-4" data-id="element-884" data-mp="3kt8pxk4haybo">
                <li className="flex items-center text-gray-700" data-id="element-885" data-mp="3ksyuvi4ku0z4">
                  <Mail size={18} className="text-teal-600 mr-3" data-id="element-886" data-mp="195luf61845cp" />
                  <a href={`mailto:${profile.email}`} className="hover:text-teal-600" data-id="element-887" data-mp="193smlnevjf34">
                    {profile.email}
                  </a>
                </li>
                <li className="flex items-center text-gray-700" data-id="element-888" data-mp="1e4q5y3bmef67">
                  <div size={18} className="text-teal-600 mr-3" data-id="element-889" data-mp="1e35dqkcs38bi" />
                  <span data-id="element-890" data-mp="1rvbplu8wet55">{profile.phone}</span>
                </li>
              </ul>
            </div>
          </div>
          {/* Right Column - Articles and Publications */}
          <div className="lg:col-span-2" data-id="element-891" data-mp="21v2mu3oufm8x">
            {/* Articles Section */}
            <div className="mb-12" data-id="element-892" data-mp="1wvzgmfx4n9zd">
              <h2 className="text-2xl font-bold text-teal-800 mb-6" data-id="element-893" data-mp="1wx145jvbxoxz">
                Articles by {profile.name}
              </h2>
              <div className="space-y-6" data-id="element-894" data-mp="7ivzk6iodf09">
                {profile.articles.map(article => <div key={article.id} className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow" data-id="element-895" data-mp="2jt51g0bmr6j">
                    <div className="md:flex" data-id="element-896" data-mp="8zru2dg5lwkm">
                      <div className="md:w-1/3" data-id="element-897" data-mp="dy37sz9kkr84">
                        <img src={article.image} alt={article.title} className="h-48 w-full object-cover" data-id="element-898" data-mp="dv7and5jq6kq" />
                      </div>
                      <div className="md:w-2/3 p-6" data-id="element-899" data-mp="3mc23dzsmqal3">
                        <div className="flex items-center text-gray-500 text-sm mb-2" data-id="element-900" data-mp="3maike1fl9lfm">
                          <Calendar size={14} className="mr-1" data-id="element-901" data-mp="3ramzbn0um83q" />
                          <span className="mr-4" data-id="element-902" data-mp="3w3px67cns3pq">{article.date}</span>
                          <Clock size={14} className="mr-1" data-id="element-903" data-mp="2yniqt82jrbn7" />
                          <span data-id="element-904" data-mp="2yn0qo0crkda0">{article.readTime}</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-905" data-mp="33ksrxkzatbqv">
                          {article.title}
                        </h3>
                        <p className="text-gray-600 mb-4 line-clamp-2" data-id="element-906" data-mp="1gpxct4ki9zlh">
                          {article.excerpt}
                        </p>
                        <Link to={`/news/${article.id}`} className="inline-flex items-center text-teal-600 hover:text-teal-800 transition-colors" data-id="element-907" data-mp="1boztgonrezie">
                          Read article <ArrowRight size={16} className="ml-1" data-id="element-908" data-mp="x1ojgr787nke" />
                        </Link>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Publications Section */}
            <div data-id="element-909" data-mp="o2jile86zi52">
              <h2 className="text-2xl font-bold text-teal-800 mb-6" data-id="element-910" data-mp="nyvtqsdozjwo">
                Academic Publications
              </h2>
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-911" data-mp="e32irdfmfyh0">
                <ul className="space-y-4" data-id="element-912" data-mp="4f6nedernzy4">
                  {profile.publications.map((pub, index) => <li key={index} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0" data-id="element-913" data-mp="9e03mdlfu51z" data-mpi={index}>
                      <p className="text-gray-700" data-id="element-914" data-mp="3rlzaxyuabrwn">{pub}</p>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* CTA Section */}
      <div className="bg-teal-50 py-16 mt-12" data-id="element-915" data-mp="s4hs25z8mehj">
        <div className="container mx-auto px-4 max-w-7xl" data-id="element-916" data-mp="s49e0ardp34r">
          <div className="max-w-3xl mx-auto text-center" data-id="element-917" data-mp="edw6uamb9sgh">
            <h2 className="text-2xl font-bold text-teal-800 mb-6" data-id="element-918" data-mp="eba02d78vgzb">
              Interested in Our Research?
            </h2>
            <p className="text-lg text-gray-600 mb-8" data-id="element-919" data-mp="164k2x894o7na">
              Explore our latest findings and discover how our science-backed
              products can support your microbiome health.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-920" data-mp="2vled3fo2pzo7">
              <Link to="/research" className="bg-teal-600 text-white hover:bg-teal-700 transition-all duration-300 px-6 py-3 rounded-md font-medium shadow-md hover:shadow-lg transform hover:-translate-y-1" data-id="element-921" data-mp="2vjl1dlylsp9c">
                Explore Research
              </Link>
              <Link to="/products" className="bg-white text-teal-700 border border-teal-700 hover:bg-teal-50 transition-all duration-300 px-6 py-3 rounded-md font-medium transform hover:-translate-y-1" data-id="element-922" data-mp="30eqoup7nmbzj">
                View Products
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer data-id="element-923" data-mp="2hl3ko8748d0x" />
    </div>;
};
export default StaffProfilePage;